import React from 'react';
import { Tag } from 'antd';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import AddressPicker from './Forms/AddressPicker';
import { LocationSearchByName } from './Forms/LocationSearch';
import { updateGroupedDraftOperations } from '../store/operations.store';
import MapViewAndErrorTrigger from './MapViewAndErrorTrigger';
import VehicleSwitcher from './VehicleSwitcher';
import ItemsEditorModal from './Forms/ItemsEditorModal';

type UpdateLocationsFn = (
  key: number,
  lng: number,
  lat: number,
  locationId: string,
  name: string,
  address: string,
  clearErrors?: boolean,
  waiting_time?: number,
) => void;

type EditingColumn = {
  title?: string;
  dataIndex?: string;
  key: string;
  width?: number | string;
  className?: string;
  isEditable?: boolean;
  render?: (value: any, data: { key: string }) => React.ReactElement;
  onValueChange?: (key: string, value: any) => void;
  editingElement?: (value: any, data: any) => JSX.Element;
};

const getOperationEditingColumns = (
  updateLocations: UpdateLocationsFn,
  dispatch: Dispatch<AnyAction>,
): EditingColumn[] => [
  {
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
    width: '35%',
    isEditable: true,
    render: (value: any, rowData: any) => {
      if (rowData.type === 'parent') {
        return (
          <div style={{ overflow: 'hidden' }}>
            {value} <Tag color={rowData.mapColor}>{rowData.children.length}</Tag>
          </div>
        );
      }
      if (rowData.isStartingRow) {
        return (
          <div style={{ overflow: 'hidden', color: '#52c41a', fontWeight: 600 }}>
            {value} <Tag color="green">Start</Tag>
          </div>
        );
      }
      if (rowData.isEndingRow) {
        return (
          <div style={{ overflow: 'hidden', color: '#ff4d4f', fontWeight: 600 }}>
            {value} <Tag color="red">End</Tag>
          </div>
        );
      }
      return <div style={{ overflow: 'hidden' }}>{value}</div>;
    },
    editingElement: (value: any, data: any) => (
      <AddressPicker
        onChange={({ lng, lat, address }) => {
          updateLocations(data.key, lng, lat, 'N/A', 'N/A', address, true);
        }}
        value={value}
      />
    ),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    isEditable: true,
    editingElement: (value: any, data: any) => (
      <LocationSearchByName
        loading={false}
        value={value}
        onChange={(locationId: string, locationName: string, address: string, location: any) => {
          updateLocations(data.key, location.lng, location.lat, locationId, locationName, address, true);
        }}
      />
    ),
    width: '25%',
  },
  {
    title: 'Items',
    key: 'items',
    dataIndex: 'items',
    width: '20%',
    isEditable: true,
    editingElement: (value: any, rowData: any) => (
      <ItemsEditorModal
        value={value ?? []}
        onChange={(items) => dispatch(updateGroupedDraftOperations({ index: rowData.key, key: 'items', data: items }))}
      />
    ),
    render: (items: any[], data: any) => {
      if (data.type === 'parent') {
        const allItems = data.children?.flatMap((c: any) => c.items ?? []) ?? [];
        const pickupTotal = allItems
          .filter((i: any) => i.operation === 'pickup')
          .reduce((sum: number, i: any) => sum + Number(i.capacity ?? 0), 0);
        const dropoffTotal = allItems
          .filter((i: any) => i.operation === 'drop_off')
          .reduce((sum: number, i: any) => sum + Number(i.capacity ?? 0), 0);
        return (
          <div>
            {pickupTotal > 0 && <div style={{ color: '#52c41a' }}>↑ Pickup: {pickupTotal}</div>}
            {dropoffTotal > 0 && <div style={{ color: '#ff4d4f' }}>↓ Drop Off: {dropoffTotal}</div>}
            {pickupTotal === 0 && dropoffTotal === 0 && <span style={{ color: '#aaa' }}>-</span>}
          </div>
        );
      }
      if (!items || items.length === 0) {
        return <span style={{ color: '#aaa' }}>No items</span>;
      }
      return (
        <div>
          {items.map((item: any) => (
            <div key={`${item.item_name}-${item.operation}-${item.capacity}`}>
              <span style={{ color: item.operation === 'pickup' ? '#52c41a' : '#ff4d4f' }}>
                {item.operation === 'pickup' ? '↑' : '↓'}
              </span>{' '}
              {item.item_name} <span style={{ fontWeight: 600 }}>({item.capacity})</span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    title: 'Value',
    key: 'value',
    dataIndex: 'value',
    width: '18%',
    isEditable: true,
    onValueChange: (index: string, event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(updateGroupedDraftOperations({ index, key: 'value', data: event.target.value }));
    },
    render: (value: string, data: any) => <>{data.type === 'parent' ? `${value}` : value}</>,
  },
  {
    title: 'Priority',
    dataIndex: 'priority',
    key: 'priority',
    width: '10%',
    render: (value: any, record: any): React.ReactElement => {
      if (record.type === 'parent') {
        return <></>;
      }
      return <span>{value ?? 0}</span>;
    },
  },
  {
    title: 'Waiting Time (min)',
    dataIndex: 'waiting_time',
    key: 'waiting_time',
    width: '12%',
    render: (value: any, record: any): React.ReactElement => {
      if (record.type === 'parent') {
        return <></>;
      }
      return <span>{value ?? '-'}</span>;
    },
  },
  {
    title: '',
    key: 'location',
    dataIndex: 'location',
    width: 50,
    className: 'location-btn',
    render: (data: any, rowValues: any): React.ReactElement => (
      <MapViewAndErrorTrigger data={data} rowValues={rowValues} />
    ),
  },
  {
    title: '',
    key: 'key',
    dataIndex: 'key',
    width: 50,
    render: (_: any, rowValues: any): React.ReactElement =>
      rowValues.type !== 'parent' ? <VehicleSwitcher data={rowValues} /> : <></>,
  },
];

export default getOperationEditingColumns;
